import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../arc/components/button/button";
import { Input } from "../arc/components/input/input";
import { PasswordField } from "../arc/components/password-field/password-field";

// Inject DM Sans, same font as CustomerDashboard
const injectFont = () => {
  if (document.getElementById("dm-sans-font")) return;
  const link = document.createElement("link");
  link.id   = "dm-sans-font";
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap";
  document.head.appendChild(link);
};

const DM_SANS = "'DM Sans', system-ui, sans-serif";
const DM_MONO = "'DM Mono', 'Fira Code', monospace";

const Login = () => {
  useEffect(() => { injectFont(); }, []);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: "#f1f2f4",
        boxShadow: "none",
        fontFamily: DM_SANS,
        WebkitFontSmoothing: "antialiased",
      }}
    >
      {/* Subtle grid overlay for depth */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 60%, rgba(12,44,84,0.08) 0%, transparent 45%), radial-gradient(circle at 85% 15%, rgba(92,113,143,0.1) 0%, transparent 45%), radial-gradient(circle at 50% 95%, rgba(131,148,171,0.07) 0%, transparent 40%)",
        }}
      />

      <div
        className="relative w-full max-w-md p-8 rounded-2xl auth-arc"
        style={{
          background: "linear-gradient(145deg, #0c2c54 0%, #0e3363 50%, #0a2d58 100%)",
          backdropFilter: "none",
          border: "1px solid rgba(131,148,171,0.2)",
          boxShadow: "0 24px 64px rgba(12,44,84,0.22), 0 4px 16px rgba(12,44,84,0.12), inset 0 1px 0 rgba(131,148,171,0.12)",
        }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div
              style={{
                width: "72px",
                height: "72px",
                borderRadius: "14px",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.18)",
                padding: "6px",
              }}
            >
              <img
                src="/assets/logo.png"
                alt="Axo Signal logo"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
          </div>
          <p style={{ fontFamily: DM_SANS, color: "#ffffff", fontSize: "16px", fontWeight: "400", marginTop: "0.5rem", letterSpacing: "0.01em" }}>
            Sign in to your account
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div
            className="mb-5 p-3 rounded-lg text-sm"
            style={{
              background: "rgba(239,68,68,0.15)",
              border: "1px solid rgba(239,68,68,0.35)",
              color: "#fca5a5",
            }}
          >
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            label="Email Address"
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@company.com"
            disabled={loading}
          />

          <PasswordField
            label="Password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="••••••••"
            disabled={loading}
          />
          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              style={{
                fontFamily: DM_SANS,
                fontSize: "13px",
                fontWeight: "500",
                color: "rgba(255,255,255,0.7)",
                textDecoration: "none",
                transition: "color 0.2s ease",
              }}
              onMouseEnter={(e) => (e.target.style.color = "#ffffff")}
              onMouseLeave={(e) => (e.target.style.color = "rgba(255,255,255,0.7)")}
            >
              Forgot password?
            </Link>
          </div>
        <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={loading}
            style={{ width: "100%", fontFamily: DM_SANS, fontWeight: 600, marginTop: 4 }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        {/* Sign up link */}
        <div
          className="mt-7 pt-6 text-center"
          style={{ borderTop: "1px solid rgba(92,113,143,0.3)" }}
        >
          <p style={{ fontFamily: DM_SANS, fontSize: "14px", color: "#ffffff" }}>
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="font-semibold transition-colors"
              style={{ color: "#ffffff" }}
              onMouseEnter={(e) => (e.target.style.color = "rgba(255,255,255,0.7)")}
              onMouseLeave={(e) => (e.target.style.color = "#ffffff")}
            >
              Sign Up
            </Link>
          </p>
         
        </div>
        
      </div>
    </div>
  );
};

export default Login;